import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const CRITERIA = [
  { label: "Real-World Usefulness", points: 30, desc: "Does it solve a genuine problem someone has today?" },
  { label: "Quality of Implementation", points: 25, desc: "Prompt quality, code health, safety rails." },
  { label: "Originality & Innovation", points: 30, desc: "Would we not have thought of this ourselves?" },
  { label: "Impact", points: 15, desc: "Time saved · tickets deflected · devs unblocked." },
];

function Bar({ item, i, inView }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      className="group"
      data-testid={`judging-criterion-${i}`}
    >
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <div>
          <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight">
            {item.label}
          </h3>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{item.desc}</p>
        </div>
        <div className="flex items-baseline gap-1 shrink-0">
          <motion.span
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: i * 0.08 + 0.4, duration: 0.4 }}
            className="font-heading text-3xl md:text-4xl font-black text-gradient-neon"
          >
            {item.points}
          </motion.span>
          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
            pts
          </span>
        </div>
      </div>
      <div className="relative h-2 rounded-full bg-white/5 overflow-hidden">
        <motion.div
          data-testid={`judging-progress-bar-${i}`}
          initial={{ width: 0 }}
          animate={inView ? { width: `${item.points}%` } : {}}
          transition={{ duration: 1.4, delay: i * 0.08 + 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#2FCC7C] to-[#2D7BF0]"
          style={{ boxShadow: "0 0 12px rgba(47,204,124,0.5)" }}
        />
      </div>
    </motion.div>
  );
}

export default function JudgingCriteria() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });

  return (
    <section
      id="judging"
      className="relative py-16 lg:py-20 px-6 md:px-12 lg:px-24"
      data-testid="judging-section"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.4fr] gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#2FCC7C] mb-4">
            // 04 · How we score
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-tight">
            Judging <span className="text-gradient-neon">criteria</span>.
          </h2>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
            A transparent{" "}
            <span className="text-white font-mono">100-point</span> rubric. Usefulness carries the most weight. This is about shipping value, not spectacle.
          </p>

          <div className="mt-10 glass rounded-2xl p-6">
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-6xl md:text-7xl font-black text-gradient-neon">
                100
              </span>
              <span className="font-mono text-sm text-zinc-500 uppercase tracking-widest">
                pts total
              </span>
            </div>
            <p className="mt-3 text-sm text-zinc-500">
              Weighted across 4 dimensions. Usefulness leads, originality close behind.
            </p>
          </div>
        </motion.div>

        <div ref={ref} className="space-y-8">
          {CRITERIA.map((c, i) => (
            <Bar key={c.label} item={c} i={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
